import React from 'react';
import { Box, Typography } from '@mui/material';
import ScrollFadeWrapper, {
  ScrollFadeSection,
} from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import { formatMarkdownRichText } from '../../../utils/formatRichText';
import Image from '../../Image/Image';

const CARD_MAX_WIDTH = 800;
const CARD_GAP = 20;
const ICON_BOX_SIZE = 120;
const TITLE_GAP = 16;
const richTextSx = {
  '& p': { m: 0, mb: '1em' },
  '& p:last-of-type': { mb: 0 },
  '& strong': { fontWeight: 700 },
  '& ul': {
    m: 0,
    mt: '1em',
    pl: '20px',
  },
  '& li': { mb: '0.5em' },
  '& a': {
    color: '#C7CB00',
    textDecoration: 'none',
    fontWeight: 700,
  },
  '& a:hover': {
    color: '#C7CB00',
    textDecoration: 'underline',
  },
};

const getAlign = (index) => (index % 2 === 0 ? 'flex-start' : 'flex-end');
const html = (value) => ({
  __html: formatMarkdownRichText(value),
});

const SnakeGuideCard = ({ card, index }) => {
  const align = getAlign(index);
  const icon = card.iconUrl;
  const title = card.title || card.heading || '';
  const body = card.body || card.description || '';

  return (
    <ScrollFadeWrapper
      key={index}
      delay={0.25 + index * 0.12}
      sx={{ width: '100%' }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: align === 'flex-start' ? 'flex-start' : 'flex-end',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: `${CARD_MAX_WIDTH}px`,
            display: 'flex',
            alignItems: 'flex-start',
            gap: '40px',
            backgroundColor: '#FFFFFF',
            borderRadius: '32px',
            boxShadow:
              '0px 1px 2px 0px rgba(162, 172, 177, 0.18), 0px 18px 36px -8px rgba(22, 47, 59, 0.12)',
            boxSizing: 'border-box',
            padding: '40px 80px 40px 40px',
            height: 'auto',
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              width: `${ICON_BOX_SIZE}px`,
              height: `${ICON_BOX_SIZE}px`,
              borderRadius: '34px',
              backgroundColor: '#C7CA00',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            {icon ? (
              <Image
                width={68}
                height={68}
                src={icon}
                alt={title || ''}
                sx={{
                  width: '4.25rem',
                  height: '4.25rem',
                  objectFit: 'contain',
                  display: 'block',
                  filter:
                    'brightness(0) saturate(100%) invert(12%) sepia(24%) saturate(1118%) hue-rotate(161deg) brightness(92%) contrast(92%)',
                }}
              />
            ) : null}
          </Box>

          <Box sx={{ width: '420px', minWidth: 0, flex: '1 1 auto' }}>
            <Typography
              component="h3"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '1.625rem',
                lineHeight: '1.875rem',
                letterSpacing: '-0.03em',
                color: '#162F3B',
                mb: '32px',
              }}
            >
              {title}
            </Typography>

            <Typography
              component="div"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: '1rem',
                lineHeight: '1.5rem',
                letterSpacing: '-0.002em',
                color: '#162F3B',
                width: '100%',
                pb: '40px',
                whiteSpace: 'pre-wrap',
                ...richTextSx,
              }}
              dangerouslySetInnerHTML={html(body)}
            />
          </Box>
        </Box>
      </Box>
    </ScrollFadeWrapper>
  );
};

const SeoSnakeGuideComponent = ({ data }) => {
  if (!data) return null;

  const { title, description, cards = [] } = data;

  return (
    <ScrollFadeSection
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',

        backgroundColor: '#F5F6F7',
        //   py: { xs: 6, md: 9 },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '1280px',
          px: { xs: 2, sm: 4, md: 6 },
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <ScrollFadeWrapper variant="text">
            <Typography
              component="h2"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '3rem',
                lineHeight: '3.25rem',
                letterSpacing: '-0.03em',
                color: '#162F3B',
                maxWidth: '1020px',
                mb: `${TITLE_GAP}px`,
                textAlign: 'center',
              }}
            >
              {title}
            </Typography>
          </ScrollFadeWrapper>

          <ScrollFadeWrapper variant="text" delay={0.12}>
            <Typography
              component="div"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: '1.25rem',
                lineHeight: '1.5rem',
                letterSpacing: '-0.03em',
                color: '#162F3B',
                maxWidth: '1080px',
                mb: '40px',
                textAlign: 'center',
                whiteSpace: 'pre-line',
                ...richTextSx,
              }}
              dangerouslySetInnerHTML={html(description)}
            />
          </ScrollFadeWrapper>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: `${CARD_GAP}px`,
          }}
        >
          {cards.map((card, index) => (
            <SnakeGuideCard key={index} card={card} index={index} />
          ))}
        </Box>
      </Box>
    </ScrollFadeSection>
  );
};

export default SeoSnakeGuideComponent;
